/**
 * Breeden-Litzenberger risk-neutral density from listed call prices.
 * p(K) ∝ exp(rT) · ∂²C/∂K²  (finite-difference on smoothed call mids).
 */
import type { OptionRow } from '../services/gexCalculator';
import type { BreedenLitzenbergerPoint, RndMoments } from '../types/marketAnalytics';

function safeNum(v: unknown): number {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function isCall(row: OptionRow): boolean {
  const t = String(row['Option Type'] ?? row.optionType ?? '').toUpperCase();
  return t.startsWith('C');
}

function callMid(row: OptionRow): number {
  const bid = safeNum(row.Bid ?? row.bid ?? row['Bid Price']);
  const ask = safeNum(row.Ask ?? row.ask ?? row['Ask Price']);
  const last = safeNum(row.Last ?? row.last ?? row['Last Price']);
  if (bid > 0 && ask > 0) return (bid + ask) / 2;
  if (last > 0) return last;
  return safeNum(row.Premium ?? row.premium ?? row['Option Premium']);
}

export function extractCallCurve(rows: OptionRow[], spot: number): { strike: number; mid: number }[] {
  const byStrike = new Map<number, number>();
  for (const row of rows) {
    if (!isCall(row)) continue;
    const strike = safeNum(row.Strike ?? row.strike ?? row['Strike Price']);
    const mid = callMid(row);
    if (!strike || mid <= 0) continue;
    const prev = byStrike.get(strike) ?? 0;
    if (mid > prev) byStrike.set(strike, mid);
  }
  return Array.from(byStrike.entries())
    .map(([strike, mid]) => ({ strike, mid }))
    .filter((p) => p.strike > spot * 0.7 && p.strike < spot * 1.3)
    .sort((a, b) => a.strike - b.strike);
}

function smooth3(points: { strike: number; mid: number }[]): { strike: number; mid: number }[] {
  if (points.length < 3) return points;
  return points.map((p, i) => {
    const a = points[Math.max(0, i - 1)];
    const b = points[i];
    const c = points[Math.min(points.length - 1, i + 1)];
    return { strike: b.strike, mid: (a.mid + b.mid + c.mid) / 3 };
  });
}

export function computeBreedenLitzenberger(
  rows: OptionRow[],
  spot: number,
  r: number = 0.05,
  tYears: number = 7 / 365,
): { curve: BreedenLitzenbergerPoint[]; moments: RndMoments | null } {
  const calls = smooth3(extractCallCurve(rows, spot));
  if (calls.length < 5 || spot <= 0) {
    return { curve: [], moments: null };
  }

  const disc = Math.exp(r * tYears);
  const curve: BreedenLitzenbergerPoint[] = [];

  for (let i = 1; i < calls.length - 1; i += 1) {
    const k0 = calls[i - 1].strike;
    const k1 = calls[i].strike;
    const k2 = calls[i + 1].strike;
    const dK1 = k1 - k0;
    const dK2 = k2 - k1;
    if (dK1 <= 0 || dK2 <= 0) continue;
    const d2C =
      ((calls[i + 1].mid - calls[i].mid) / dK2 - (calls[i].mid - calls[i - 1].mid) / dK1) /
      ((dK1 + dK2) / 2);
    const density = Math.max(0, disc * d2C);
    curve.push({ strike: k1, density, call_mid: calls[i].mid });
  }

  const total = curve.reduce((s, p) => s + p.density, 0);
  if (total <= 0) return { curve: [], moments: null };

  const norm = curve.map((p) => ({ ...p, density: p.density / total }));
  let mean = 0;
  for (const p of norm) mean += p.strike * p.density;
  let m2 = 0;
  let m3 = 0;
  let m4 = 0;
  for (const p of norm) {
    const d = p.strike - mean;
    m2 += p.density * d * d;
    m3 += p.density * d ** 3;
    m4 += p.density * d ** 4;
  }
  const variance = Math.max(m2, 1e-8);
  const std = Math.sqrt(variance);
  const skewness = m3 / (std ** 3);
  const kurtosis = m4 / (variance ** 2);
  const leftTail = norm.filter((p) => p.strike < mean * 0.95).reduce((s, p) => s + p.density, 0);
  const rightTail = norm.filter((p) => p.strike > mean * 1.05).reduce((s, p) => s + p.density, 0);
  const tail_ratio = rightTail > 1e-6 ? leftTail / rightTail : leftTail * 100;

  const atmIv = rows
    .filter((row) => {
      const k = safeNum(row.Strike ?? row.strike);
      return k > 0 && Math.abs(k - spot) / spot < 0.02;
    })
    .map((row) => safeNum(row['Implied Volatility'] ?? row.iv ?? row.implied_volatility))
    .filter((v) => v > 0);
  const implied_vol = atmIv.length ? atmIv.reduce((a, b) => a + b, 0) / atmIv.length : null;

  let regime_skew: RndMoments['regime_skew'] = 'neutral';
  if (skewness < -0.8) regime_skew = 'bearish';
  else if (skewness > 0.4) regime_skew = 'bullish';

  return {
    curve: norm,
    moments: {
      forward: mean,
      forward_pct: spot > 0 ? ((mean - spot) / spot) * 100 : 0,
      implied_vol,
      skewness,
      kurtosis,
      tail_ratio,
      regime_skew,
    },
  };
}
